<template>
  <div class="event-page">
    <header class="page-header">
      <div class="header-content">
        <button class="back-btn" @click="router.back()">
          <ChevronLeft size="24" />
        </button>
        <h1 class="page-title">赛事中心</h1>
        <div class="header-actions">
          <Search size="20" />
        </div>
      </div>
    </header>

    <div class="event-content">
      <section class="event-banner">
        <div class="banner-content">
          <h2>星屿小筑五月电竞联赛</h2>
          <p>5月20举办 · 第一名队伍奖励1000r</p>
          <button class="banner-btn" @click="handleRegisterClick">立即报名</button>
        </div>
      </section>

      <section class="event-tabs">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'ongoing' }"
          @click="activeTab = 'ongoing'"
        >
          进行中
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'upcoming' }"
          @click="activeTab = 'upcoming'"
        >
          即将开始
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'ended' }"
          @click="activeTab = 'ended'"
        >
          已结束
        </button>
      </section>

      <section class="event-list">
        <div class="event-card" v-for="event in currentEvents" :key="event.id">
          <div class="event-image">
            <img :src="event.image" :alt="event.title" />
            <div class="event-status" :class="event.status">{{ event.statusText }}</div>
          </div>
          <div class="event-info">
            <h3 class="event-title">{{ event.title }}</h3>
            <div class="event-meta">
              <span class="meta-item">
                <Calendar size="14" /> {{ event.date }}
              </span>
              <span class="meta-item">
                <Clock size="14" /> {{ event.time }}
              </span>
            </div>
            <p class="event-desc">{{ event.description }}</p>
            <div class="event-footer">
              <span class="prize">
                <Trophy size="14" /> {{ event.prize }}
              </span>
              <button
                class="event-btn"
                :class="{ disabled: event.status === 'ended', registered: event.isRegistered }"
                @click="handleRegisterClick(event)"
              >
                {{ event.status === 'ended' ? '已结束' : (event.isRegistered ? '已报名' : '报名') }}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div class="modal-overlay" v-if="showModal" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <AlertCircle size="48" class="modal-icon" />
          <h3>确认报名</h3>
        </div>
        <div class="modal-body">
          <p class="requirement-text">确定要报名参加 <strong>{{ selectedEvent?.title }}</strong> 赛事吗？</p>
        </div>
        <div class="modal-footer">
          <button class="modal-btn primary" @click="confirmRegistration">
            确认报名
          </button>
          <button class="modal-btn" @click="closeModal">
            取消
          </button>
        </div>
      </div>
    </div>

    <div class="toast" v-if="showToast" :class="toastType">
      {{ toastMessage }}
    </div>

    <nav class="bottom-nav">
      <div class="nav-item" @click="router.push('/')">
        <Home size="22" />
        <span>首页</span>
      </div>
      <div class="nav-item" @click="router.push('/hold')">
        <Zap size="22" />
        <span>接单</span>
      </div>
      <div class="nav-item" @click="router.push('/mypage')">
        <User size="22" />
        <span>我的</span>
      </div>
    </nav>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ChevronLeft, Search, Calendar, Clock, Trophy, Home, Zap, User, AlertCircle } from 'lucide-vue-next'
import { checkEventRegistration, registerForEvent } from '@/api/events'
import { getSecureStorage, SECURE_STORAGE_KEY, PLATFORM_TABLE_MAP } from '@/utils/security'
import { supabase } from '@/utils/supabase'

const router = useRouter()
const activeTab = ref('ongoing')
const showModal = ref(false)
const showToast = ref(false)
const toastMessage = ref('')
const toastType = ref('')
const selectedEvent = ref(null)

const events = ref([
  {
    id: 'xingyu_cup_s1',
    title: '星屿杯S1赛季',
    date: '2026-05-20',
    time: '20:00',
    description: '参与星屿杯S1赛季，争夺千元奖金池！',
    prize: '奖金 ¥1000',
    image: new URL('@/assets/img/carousel_img/1.jpg', import.meta.url).href,
    status: 'ongoing',
    statusText: '进行中',
    isRegistered: false
  },
  {
    id: 'weekend_challenge',
    title: '周末娱乐奖金赛',
    date: '2026-05-02',
    time: '20:00',
    description: '每周六固定举办的娱乐赛，周榜前三角逐',
    prize: '奖金 ¥200',
    image: new URL('@/assets/img/carousel_img/2.jpg', import.meta.url).href,
    status: 'upcoming',
    statusText: '即将开始',
    isRegistered: false
  }
])

// 初始化检查用户报名状态
async function initUserRegistrations() {
  const userInfo = getSecureStorage(SECURE_STORAGE_KEY)
  if (!userInfo || !userInfo.username) return

  for (const event of events.value) {
    const { isRegistered, error } = await checkEventRegistration(event.id, userInfo.username)
    if (!error && isRegistered) {
      event.isRegistered = true
    }
  }
}

// 检查用户是否在周榜前十
async function isInWeeklyTopTen(username) {
  try {
    const now = new Date()
    const dayOfWeek = now.getDay() || 7
    const startOfWeek = new Date(now)
    startOfWeek.setDate(now.getDate() - dayOfWeek + 1)
    startOfWeek.setHours(0, 0, 0, 0)

    const endOfWeek = new Date(startOfWeek)
    endOfWeek.setDate(startOfWeek.getDate() + 6)
    endOfWeek.setHours(23, 59, 59, 999)

    const savedUser = getSecureStorage(SECURE_STORAGE_KEY)
    const platform = savedUser?.platform || 'mobile_delta'
    const ordersTable = PLATFORM_TABLE_MAP[platform]?.orders || 'orders'

    const { data: orders, error } = await supabase
      .from(ordersTable)
      .select('handler_id, price, finished_at')
      .in('status', ['completed', 'finished', 'pending_review'])
      .gte('finished_at', startOfWeek.toISOString())
      .lte('finished_at', endOfWeek.toISOString())

    if (error) {
      console.error('获取周榜数据失败:', error)
      return false
    }

    const playerEarnings = {}
    orders.forEach(order => {
      if (order.handler_id) {
        if (!playerEarnings[order.handler_id]) {
          playerEarnings[order.handler_id] = 0
        }
        playerEarnings[order.handler_id] += Math.floor(Number(order.price) * 0.83)
      }
    })

    const sortedPlayers = Object.entries(playerEarnings)
      .map(([name, earnings]) => ({ name, earnings }))
      .sort((a, b) => b.earnings - a.earnings)
      .slice(0, 10)

    return sortedPlayers.some(player => player.name === username)
  } catch (error) {
    console.error('检查周榜排名失败:', error)
    return false
  }
}

// 检查用户本月接单数量是否超过10单
async function hasMonthlyOrdersOverTen(username) {
  try {
    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)

    const savedUser = getSecureStorage(SECURE_STORAGE_KEY)
    const platform = savedUser?.platform || 'mobile_delta'
    const ordersTable = PLATFORM_TABLE_MAP[platform]?.orders || 'orders'

    const { data: orders, error } = await supabase
      .from(ordersTable)
      .select('id')
      .eq('handler_id', username)
      .in('status', ['completed', 'finished', 'pending_review'])
      .gte('finished_at', startOfMonth.toISOString())

    if (error) {
      console.error('获取月订单数据失败:', error)
      return false
    }

    return orders && orders.length >= 10
  } catch (error) {
    console.error('检查月接单数量失败:', error)
    return false
  }
}

onMounted(() => {
  initUserRegistrations()
})

const currentEvents = computed(() => {
  return events.value.filter(event => event.status === activeTab.value)
})

async function handleRegisterClick(event) {
  if (event.status === 'ended') return
  if (event.isRegistered) {
    showToastMessage('您已报名该赛事', 'warning')
    return
  }

  selectedEvent.value = event

  const userInfo = getSecureStorage(SECURE_STORAGE_KEY)
  if (!userInfo || !userInfo.username) {
    showToastMessage('请先登录', 'error')
    return
  }

  // 根据赛事类型检查报名条件
  let canRegister = true
  let errorMessage = ''

  if (event.id === 'weekend_challenge') {
    // 周末挑战赛：需要周榜前十
    const isTopTen = await isInWeeklyTopTen(userInfo.username)
    if (!isTopTen) {
      canRegister = false
      errorMessage = '周赛需要周榜前十才能报名'
    }
  } else if (event.id === 'xingyu_cup_s1') {
    // 星屿杯：需要月接十单以上
    const hasEnoughOrders = await hasMonthlyOrdersOverTen(userInfo.username)
    if (!hasEnoughOrders) {
      canRegister = false
      errorMessage = '星屿杯需要月接十单以上才能报名'
    }
  }

  if (!canRegister) {
    showToastMessage(errorMessage, 'error')
    return
  }

  showModal.value = true
}

function closeModal() {
  showModal.value = false
  selectedEvent.value = null
}

const isRegistering = ref(false)
async function confirmRegistration() {
  if (isRegistering.value) return
  isRegistering.value = true
  try {
    const userInfo = getSecureStorage(SECURE_STORAGE_KEY)
    if (!userInfo || !userInfo.username) {
      showToastMessage('请先登录', 'error')
      closeModal()
      return
    }

    if (!selectedEvent.value) {
      showToastMessage('请选择要报名的赛事', 'error')
      closeModal()
      return
    }

    // 先检查是否已报名
    const { isRegistered: alreadyRegistered, error: checkError } = await checkEventRegistration(
      selectedEvent.value.id,
      userInfo.username
    )

    if (checkError) {
      showToastMessage('检查报名状态失败，请稍后重试', 'error')
      closeModal()
      return
    }

    if (alreadyRegistered) {
      showToastMessage('您已报名该赛事', 'warning')
      const eventIndex = events.value.findIndex(e => e.id === selectedEvent.value.id)
      if (eventIndex !== -1) {
        events.value[eventIndex].isRegistered = true
      }
      closeModal()
      return
    }

    // 执行报名
    const { success, error } = await registerForEvent(
      selectedEvent.value.id,
      userInfo.username,
      userInfo.username
    )

    if (error) {
      showToastMessage('报名失败：' + error.message, 'error')
    } else if (success) {
      const eventIndex = events.value.findIndex(e => e.id === selectedEvent.value.id)
      if (eventIndex !== -1) {
        events.value[eventIndex].isRegistered = true
      }
      showToastMessage('报名成功！', 'success')
    }

    closeModal()
  } catch (error) {
    console.error('报名失败:', error)
    showToastMessage('报名失败，请稍后重试', 'error')
  } finally {
    isRegistering.value = false
  }
}

function showToastMessage(message, type) {
  toastMessage.value = message
  toastType.value = type
  showToast.value = true
  setTimeout(() => {
    showToast.value = false
  }, 3000)
}
</script>

<style scoped>
.event-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%);
  padding-bottom: 80px;
}

.page-header {
  padding: 20px;
  background: rgba(0, 0, 0, 0.3);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
}

.page-title {
  color: #fff;
  font-size: 18px;
  font-weight: 600;
}

.header-actions {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.event-content {
  padding: 20px;
}

.event-banner {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 20px;
  color: #fff;
}

.banner-content h2 {
  font-size: 24px;
  margin-bottom: 8px;
}

.banner-content p {
  opacity: 0.8;
  margin-bottom: 20px;
}

.banner-btn {
  background: #fff;
  color: #667eea;
  border: none;
  padding: 10px 24px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
}

.event-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.tab-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  cursor: pointer;
  transition: all 0.3s;
}

.tab-btn.active {
  background: #667eea;
  color: #fff;
}

.event-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.event-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  overflow: hidden;
}

.event-image {
  position: relative;
  height: 120px;
}

.event-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.event-status {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.event-status.ongoing {
  background: #10b981;
  color: #fff;
}

.event-status.upcoming {
  background: #f59e0b;
  color: #fff;
}

.event-status.ended {
  background: #6b7280;
  color: #fff;
}

.event-info {
  padding: 16px;
}

.event-title {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
}

.event-meta {
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
}

.event-desc {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  margin-bottom: 12px;
}

.event-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.prize {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #fbbf24;
  font-size: 13px;
}

.event-btn {
  background: #667eea;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  cursor: pointer;
}

.event-btn.disabled {
  background: #6b7280;
  cursor: not-allowed;
}

.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  background: rgba(0, 0, 0, 0.8);
  padding: 10px;
  gap: 10px;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 8px;
}

.nav-item.active {
  color: #667eea;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 16px;
  padding: 30px;
  width: 100%;
  max-width: 340px;
  text-align: center;
  border: 1px solid rgba(102, 126, 234, 0.3);
}

.modal-header {
  margin-bottom: 24px;
}

.modal-icon {
  color: #f59e0b;
  margin-bottom: 16px;
}

.modal-header h3 {
  color: #fff;
  font-size: 20px;
  margin: 0;
}

.modal-body {
  margin-bottom: 24px;
}

.requirement-text {
  color: rgba(255, 255, 255, 0.9);
  font-size: 15px;
  margin-bottom: 20px;
}

.requirement-text strong {
  color: #667eea;
  font-size: 18px;
}

.current-status {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.status-label {
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  display: block;
  margin-bottom: 8px;
}

.status-value {
  color: #10b981;
  font-size: 24px;
  font-weight: 700;
}

.status-value.insufficient {
  color: #ef4444;
}

.requirement-hint {
  color: #f59e0b;
  font-size: 14px;
}

.requirement-hint strong {
  color: #fff;
}

.success-hint {
  color: #10b981;
  font-size: 14px;
}

.modal-footer {
  display: flex;
  gap: 12px;
}

.modal-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  transition: all 0.3s;
}

.modal-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.modal-btn.primary:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.toast {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.9);
  color: #fff;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 14px;
  z-index: 2000;
  animation: toastFade 3s ease-in-out;
}

.toast.success {
  border: 1px solid #10b981;
  color: #10b981;
}

.toast.error {
  border: 1px solid #ef4444;
  color: #ef4444;
}

.toast.warning {
  border: 1px solid #f59e0b;
  color: #f59e0b;
}

.event-btn.registered {
  background: #10b981;
}
</style>